import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/Icon';
import { driverImgUrl, F1_DRIVERS } from '../constants/f1Drivers';
import { F1_CONSTRUCTORS } from '../constants/f1Constructors';
import { getLevelInfo, getGrade } from '../utils/xpUtils';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const StatusBadge = ({ race }) => {
  if (!race) return null;
  if (race.isLocked && race.results?.winner)
    return <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded-full">Terminé</span>;
  if (race.isLocked)
    return <span className="text-xs bg-orange-900/30 text-orange-400 border border-orange-800 px-2 py-0.5 rounded-full">Verrouillé</span>;
  const deadline = race.deadline ? new Date(race.deadline) : null;
  if (deadline && new Date() > deadline)
    return <span className="text-xs bg-orange-900/30 text-orange-400 border border-orange-800 px-2 py-0.5 rounded-full">Verrouillé</span>;
  return <span className="text-xs bg-f1red/10 text-f1red border border-f1red/30 px-2 py-0.5 rounded-full">Modifiable</span>;
};

const PickItem = ({ label, value }) => {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-zinc-500 min-w-[140px]">{label}</span>
      <span className="text-white font-medium">
        {Array.isArray(value) ? value.join(', ') : value}
      </span>
    </div>
  );
};

const BREAKDOWN_LABELS = {
  winner: 'Vainqueur', p2: 'P2', p3: 'P3',
  fastestLap: 'Meilleur tour course', polePosition: 'Pole',
  fastestQualifyingLap: 'Meilleur tour qualif',
  q1Eliminated: 'Q1 éliminés', q2Eliminated: 'Q2 éliminés',
  safetyCar: 'Safety Car', virtualSafetyCar: 'Virtual Safety Car', redFlag: 'Drapeau rouge',
};

const PronosticCard = ({ pronostic }) => {
  const { race, picks, score, scoreBreakdown, sprintPicks, sprintScore, sprintScoreBreakdown } = pronostic;
  const [expanded, setExpanded] = useState(false);
  const isOpen = race && !race.isLocked && (!race.deadline || new Date() < new Date(race.deadline));

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-zinc-500">Round {race?.round} · {race?.season}</span>
            <StatusBadge race={race} />
          </div>
          <h3 className="font-bold text-white text-lg truncate">{race?.raceName || '—'}</h3>
          <p className="text-zinc-500 text-xs mt-0.5">{race?.circuit?.country} · Course : {formatDate(race?.sessions?.race?.date)}</p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {score !== null && score !== undefined ? (
            <span className="text-2xl font-black text-f1red">{score} pts</span>
          ) : (
            <span className="text-zinc-500 text-sm">Score en attente</span>
          )}
          {isOpen && race?._id && (
            <Link to={`/grand-prix/${race._id}/pronostic`} className="btn-secondary text-xs py-1 px-3">
              Modifier →
            </Link>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="mt-3 text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
      >
        <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={13} className="inline mr-1" />
        {expanded ? 'Masquer' : 'Voir mes picks'}
      </button>

      {expanded && (picks || sprintPicks) && (
        <div className="mt-3 pt-3 border-t border-zinc-800 flex flex-col gap-1.5">
          {picks && (() => {
            const results = race?.results;
            const hasResults = results?.winner != null || results?.safetyCar != null;
            return [['Vainqueur','winner'],['P2','p2'],['P3','p3'],
              ['Meilleur tour course','fastestLap'],['Pole Position','polePosition'],
              ['Meilleur tour qualif','fastestQualifyingLap'],
              ['Q1 éliminés','q1Eliminated'],['Q2 éliminés','q2Eliminated'],
              ['Safety Car','safetyCar'],['Virtual Safety Car','virtualSafetyCar'],['Drapeau rouge','redFlag'],
            ].map(([label, key]) => {
              const val = picks[key];
              const isBool = typeof val === 'boolean';
              const isArr = Array.isArray(val);
              const hasVal = isBool ? true : isArr ? val.length > 0 : !!val;
              if (!hasVal) return null;

              const resultVal = results?.[key];
              const hasResult = hasResults && (isBool ? resultVal != null : isArr ? Array.isArray(resultVal) : !!resultVal);
              const isCorrect = hasResult && (isArr
                ? false
                : isBool ? val === resultVal : val === resultVal);

              const displayPick = isBool ? (val ? 'Oui' : 'Non') : isArr ? val.join(', ') : val;
              const displayResult = hasResult && !isArr
                ? (isBool ? (resultVal ? 'Oui' : 'Non') : resultVal)
                : null;

              const pts = scoreBreakdown?.[key];

              return (
                <div key={key} className="flex items-start justify-between gap-2 py-0.5">
                  <span className="text-zinc-500 text-xs shrink-0">{label}</span>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    <span className={`text-xs font-medium text-right ${
                      !hasResult ? 'text-white' :
                      isArr ? 'text-white' :
                      isCorrect ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {displayPick}
                    </span>
                    {hasResult && !isArr && !isCorrect && displayResult && (
                      <>
                        <span className="text-zinc-600 text-[10px]">→</span>
                        <span className="text-green-400 text-xs font-medium">{displayResult}</span>
                      </>
                    )}
                    {pts != null && (
                      <span className="text-[10px] font-black text-green-400 bg-green-900/20 px-1.5 py-0.5 rounded shrink-0">+{pts}</span>
                    )}
                  </div>
                </div>
              );
            });
          })()}

          {sprintPicks && (sprintPicks.winner || sprintPicks.p2 || sprintPicks.p3) && (
            <div className="mt-2 pt-2 border-t border-zinc-800/60">
              <p className="text-[10px] text-orange-400 uppercase tracking-widest font-medium mb-1.5 flex items-center gap-1">
                <Icon name="lightning" size={10} /> Sprint
                {sprintScore != null && <span className="text-orange-400 font-black ml-auto">{sprintScore} pts</span>}
              </p>
              {[['Vainqueur sprint','winner'],['P2 sprint','p2'],['P3 sprint','p3']].map(([label, key]) => {
                const val = sprintPicks[key];
                if (!val) return null;
                const pts = sprintScoreBreakdown?.[key];
                const resultVal = race?.sprintResults?.[key];
                const hasResult = !!resultVal;
                const isCorrect = hasResult && val === resultVal;
                return (
                  <div key={key} className="flex items-start justify-between gap-2 py-0.5">
                    <span className="text-zinc-500 text-xs shrink-0">{label}</span>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs font-medium ${!hasResult ? 'text-white' : isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                        {val}
                      </span>
                      {hasResult && !isCorrect && (
                        <>
                          <span className="text-zinc-600 text-[10px]">→</span>
                          <span className="text-green-400 text-xs font-medium">{resultVal}</span>
                        </>
                      )}
                      {pts != null && (
                        <span className="text-[10px] font-black text-green-400 bg-green-900/20 px-1.5 py-0.5 rounded shrink-0">+{pts}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SEASON = 2026;

const XpHistoryChart = () => {
  const [log, setLog] = React.useState(null);
  React.useEffect(() => {
    api.get('/users/me/xp-history').then(r => setLog(r.data)).catch(() => {});
  }, []);

  if (!log) return null;
  const hasData = log.some(d => d.xp > 0);
  if (!hasData) return null;

  const maxXp  = Math.max(...log.map(d => d.xp), 1);
  const W = 300, H = 60;
  const pts = log.map((d, i) => {
    const x = (i / (log.length - 1)) * W;
    const y = H - (d.xp / maxXp) * H;
    return `${x},${y}`;
  }).join(' ');
  const area = `0,${H} ${pts} ${W},${H}`;

  const fmt = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };
  const totalPeriod = log.reduce((s, d) => s + d.xp, 0);
  const activeDays  = log.filter(d => d.xp > 0).length;
  const peakDay     = log.reduce((best, d) => d.xp > (best?.xp || 0) ? d : best, null);

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-white text-sm flex items-center gap-2">
          <span>📈</span> XP — 30 derniers jours
        </h3>
        <span className="text-xs text-zinc-500">
          <span className="text-white font-bold">{totalPeriod.toLocaleString()} XP</span> · {activeDays} jours actifs
        </span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16" preserveAspectRatio="none">
        <defs>
          <linearGradient id="xpg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#a855f7" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#xpg)" />
        <polyline points={pts} fill="none" stroke="#a855f7" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {log.map((d, i) => d.xp > 0 && (
          <circle key={i}
            cx={(i / (log.length - 1)) * W}
            cy={H - (d.xp / maxXp) * H}
            r={d === peakDay ? 3 : 2}
            fill={d === peakDay ? '#facc15' : '#a855f7'}
          />
        ))}
      </svg>

      <div className="flex justify-between mt-1.5 mb-3">
        <span className="text-[9px] text-zinc-600">{fmt(log[0].date)}</span>
        <span className="text-[9px] text-zinc-600">{fmt(log[log.length - 1].date)}</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl bg-zinc-800 p-2.5 text-center">
          <p className="text-sm font-black text-white">{totalPeriod.toLocaleString()}</p>
          <p className="text-[10px] text-zinc-500">XP total</p>
        </div>
        <div className="rounded-xl bg-zinc-800 p-2.5 text-center">
          <p className="text-sm font-black text-white">{activeDays}</p>
          <p className="text-[10px] text-zinc-500">Jours actifs</p>
        </div>
        <div className="rounded-xl bg-zinc-800 p-2.5 text-center">
          <p className="text-sm font-black" style={{ color: '#facc15' }}>{peakDay ? peakDay.xp.toLocaleString() : '—'}</p>
          <p className="text-[10px] text-zinc-500">Pic journalier</p>
        </div>
      </div>
    </div>
  );
};

const XPBar = ({ user }) => {
  const xp = user?.xp ?? 0;
  const { level, currentXP, nextLevelXP, progress } = getLevelInfo(xp);
  const grade = getGrade(level);
  const [displayed, setDisplayed] = React.useState(0);
  React.useEffect(() => {
    const t = setTimeout(() => setDisplayed(progress), 200);
    return () => clearTimeout(t);
  }, [progress]);
  const levelColors = ['#a1a1aa','#84cc16','#facc15','#fb923c','#f97316','#ef4444','#e10600','#dc2626','#b91c1c','#991b1b'];
  const color = levelColors[Math.min(level, levelColors.length - 1)];
  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-sm border-2" style={{ borderColor: color, color, backgroundColor: `${color}18` }}>{level}</div>
          <div>
            <p className="text-white font-bold text-sm">Niveau {level}</p>
            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded"
              style={{ color: grade.color, background: grade.bg }}>{grade.label}</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-zinc-400 text-xs">XP total</p>
          <p className="text-white font-black">{xp.toLocaleString()}</p>
        </div>
      </div>
      <div className="w-full h-3 bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
          style={{ width: `${Math.min(displayed * 100, 100)}%`, background: `linear-gradient(90deg, ${color}99, ${color})` }}>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_2s_infinite]" />
        </div>
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-[10px] text-zinc-600">Niv. {level}</span>
        <span className="text-[10px] text-zinc-600">Niv. {level + 1} — encore {(nextLevelXP - currentXP).toLocaleString()} XP</span>
      </div>
    </div>
  );
};

const posColor = (p) => p === 1 ? 'text-yellow-400' : p <= 3 ? 'text-orange-400' : 'text-zinc-400';

const ScoreChart = ({ pronostics }) => {
  const scored = [...pronostics].filter(p => p.score !== null && p.score !== undefined && p.race?.results?.winner).reverse();
  if (scored.length < 2) return null;
  const scores = scored.map(p => (p.score || 0) + (p.sprintScore || 0));
  const max = Math.max(...scores, 1);
  const W = 100, H = 48;
  const pts = scores.map((s, i) => `${(i / (scores.length - 1)) * W},${H - (s / max) * H}`).join(' ');
  const avg = Math.round(scores.reduce((a,b)=>a+b,0)/scores.length);
  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-white text-sm flex items-center gap-2"><Icon name="flag" size={13} className="text-f1red" />Historique de scores</h3>
        <span className="text-xs text-zinc-500">Moy. <span className="text-white font-bold">{avg} pts</span></span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16" preserveAspectRatio="none">
        <defs>
          <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e10600" stopOpacity="0.3"/>
            <stop offset="100%" stopColor="#e10600" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <polygon points={`0,${H} ${pts} ${W},${H}`} fill="url(#sg)" />
        <polyline points={pts} fill="none" stroke="#e10600" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {scores.map((s,i) => (
          <circle key={i} cx={(i/(scores.length-1))*W} cy={H-(s/max)*H} r="2" fill={s===Math.max(...scores)?'#facc15':'#e10600'} />
        ))}
      </svg>
      <div className="flex justify-between mt-1">
        {scored.map((p,i) => (
          <span key={i} className="text-[9px] text-zinc-600 truncate" style={{maxWidth:`${100/scored.length}%`}}>{p.race?.raceName?.replace('Grand Prix','GP')}</span>
        ))}
      </div>
    </div>
  );
};

const RivalSection = ({ myId }) => {
  const [friends, setFriends] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setting, setSetting] = useState(false);

  useEffect(() => {
    api.get('/users/me/rival-stats').then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
    api.get('/friends').then(r => setFriends(r.data?.friends || r.data || [])).catch(() => {});
  }, []);

  const setRival = async (rivalId) => {
    setSetting(true);
    try {
      await api.put('/users/me/rival', { rivalId });
      const r = await api.get('/users/me/rival-stats');
      setStats(r.data);
    } catch {}
    setSetting(false);
  };

  const clearRival = async () => {
    setSetting(true);
    try { await api.put('/users/me/rival', { rivalId: null }); setStats(null); } catch {}
    setSetting(false);
  };

  if (loading) return null;

  return (
    <div className="card mb-6">
      <h3 className="font-bold text-white text-sm flex items-center gap-2 mb-3">
        <span>⚔️</span> Rival désigné
      </h3>
      {!stats ? (
        <div>
          <p className="text-zinc-500 text-xs mb-3">Choisis un adversaire pour tracker qui gagne le plus entre vous.</p>
          {friends.length > 0 ? (
            <div className="flex flex-col gap-2">
              {friends.slice(0,5).map(f => (
                <button key={f._id} onClick={() => setRival(f._id)} disabled={setting}
                  className="flex items-center gap-3 py-2 px-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 transition-colors text-left">
                  <div className="w-7 h-7 rounded-full bg-f1red/20 flex items-center justify-center text-f1red text-xs font-black shrink-0">
                    {f.username?.[0]?.toUpperCase()}
                  </div>
                  <span className="text-white text-sm font-medium">{f.username}</span>
                  <span className="ml-auto text-xs text-zinc-500">Désigner →</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-zinc-600 text-xs">Ajoute des amis d'abord pour pouvoir choisir un rival.</p>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-f1red/20 flex items-center justify-center text-f1red font-black shrink-0">
              {stats.rival?.username?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="text-white font-black">{stats.rival?.username}</p>
              <p className="text-zinc-500 text-xs">Rival actuel</p>
            </div>
            <button onClick={clearRival} disabled={setting} className="text-xs text-zinc-500 hover:text-red-400 transition-colors">Changer</button>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="rounded-xl bg-zinc-800 p-3 text-center">
              <p className="text-xl font-black text-f1red">{stats.myTotal}</p>
              <p className="text-[10px] text-zinc-500">Mes pts</p>
            </div>
            <div className="rounded-xl bg-zinc-800 p-3 text-center flex flex-col items-center justify-center">
              <p className="text-xs font-black text-zinc-400">{stats.meWins}W – {stats.rivalWins}W</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">Victoires GP</p>
            </div>
            <div className="rounded-xl bg-zinc-800 p-3 text-center">
              <p className="text-xl font-black text-zinc-300">{stats.rivalTotal}</p>
              <p className="text-[10px] text-zinc-500">Ses pts</p>
            </div>
          </div>
          {stats.myTotal > stats.rivalTotal
            ? <p className="text-green-400 text-xs font-bold text-center">Tu mènes de {stats.myTotal - stats.rivalTotal} pts 🔥</p>
            : stats.rivalTotal > stats.myTotal
            ? <p className="text-red-400 text-xs font-bold text-center">{stats.rival?.username} mène de {stats.rivalTotal - stats.myTotal} pts 💀</p>
            : <p className="text-zinc-400 text-xs font-bold text-center">Égalité parfaite ⚖️</p>
          }
        </div>
      )}
    </div>
  );
};

const Profile = () => {
  const { user, logout } = useAuth();
  const [pronostics, setPronostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverStats, setDriverStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [badges, setBadges] = useState([]);
  const [newBadges, setNewBadges] = useState([]);
  const [badgeDefs, setBadgeDefs] = useState([]);
  const [seasonPrediction, setSeasonPrediction] = useState(null);
  const [seasonInfo, setSeasonInfo] = useState(null);

  useEffect(() => {
    api.get('/pronostics/me/all')
      .then(res => setPronostics(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get('/pronostics/me/badges')
      .then(res => setBadges(res.data))
      .catch(() => {});
    api.post('/users/me/recompute-badges')
      .then(res => setNewBadges(res.data.badges || []))
      .catch(() => {});
    api.get('/users/badges/defs')
      .then(res => setBadgeDefs(res.data || []))
      .catch(() => {});
    api.get('/season/my-prediction')
      .then(res => setSeasonPrediction(res.data))
      .catch(() => {});
    api.get('/season')
      .then(res => setSeasonInfo(res.data))
      .catch(() => {});
  }, []);

  const currentDriver = user?.seasonDriver
    ? (F1_DRIVERS.find(d => d.code === user.seasonDriver.code || d.name === user.seasonDriver.name) || user.seasonDriver)
    : user?.favoriteDriver
    ? (F1_DRIVERS.find(d => d.name === user.favoriteDriver.name) || user.favoriteDriver)
    : null;

  useEffect(() => {
    if (!currentDriver?.number) return;
    setStatsLoading(true);
    api.get(`/races/driver-stats/${currentDriver.number}?season=${SEASON}`)
      .then(res => setDriverStats(res.data))
      .catch(() => setDriverStats(null))
      .finally(() => setStatsLoading(false));
  }, [currentDriver?.number]);

  const pronosticScore = pronostics.reduce((acc, p) => acc + (p.score || 0), 0);
  const seasonDriverPoints = user?.seasonDriverPoints || 0;
  const totalScore = pronosticScore + seasonDriverPoints;
  const scored = pronostics.filter(p => p.score !== null && p.score !== undefined);

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="card mb-8 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full overflow-hidden border border-f1red/40 shrink-0">
            {user?.avatar
              ? <img src={user.avatar} alt="avatar" className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-f1red/20 flex items-center justify-center text-2xl font-black text-f1red">{user?.username?.[0]?.toUpperCase()}</div>
            }
          </div>
          <div>
            <h1 className="text-2xl font-black text-white">{user?.username}</h1>
            <p className="text-zinc-500 text-sm">{user?.email}</p>
            {user?.role === 'admin' && (
              <span className="text-xs bg-f1red/20 text-f1red border border-f1red/30 px-2 py-0.5 rounded-full font-medium">Admin</span>
            )}
          </div>
        </div>
        <button onClick={logout} className="btn-secondary text-sm py-1.5 px-3 shrink-0">Déconnexion</button>
      </div>

      <XPBar user={user} />
      <XpHistoryChart />

      {user?.circuitTitles?.length > 0 && (
        <div className="card mb-6">
          <h3 className="font-bold text-white text-sm mb-3 flex items-center gap-2">
            <Icon name="trophy" size={14} /> Titres de champion de circuit
          </h3>
          <div className="flex flex-wrap gap-2">
            {[...user.circuitTitles].reverse().map((t, i) => (
              <div key={i} className="flex items-center gap-1.5 bg-yellow-400/8 border border-yellow-400/20 rounded-lg px-3 py-1.5">
                <Icon name="star" size={11} />
                <div>
                  <p className="text-white text-xs font-bold leading-tight">{t.raceName} {t.season}</p>
                  <p className="text-yellow-400/70 text-[10px]">{t.score} pts</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ScoreChart pronostics={pronostics} />

      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="card text-center">
          <p className="text-3xl font-black text-white">{pronostics.length}</p>
          <p className="text-xs text-zinc-500 mt-1">Pronostics soumis</p>
        </div>
        <div className="card text-center">
          <p className="text-3xl font-black text-f1red">{totalScore}</p>
          <p className="text-xs text-zinc-500 mt-1">Points au total</p>
          {seasonDriverPoints > 0 && (
            <p className="text-[10px] text-zinc-500 mt-0.5">{pronosticScore} pronos + {seasonDriverPoints} pilote</p>
          )}
        </div>
        <div className="card text-center">
          <p className="text-3xl font-black text-white">
            {scored.length > 0 ? Math.round(pronosticScore / scored.length) : '—'}
          </p>
          <p className="text-xs text-zinc-500 mt-1">Moyenne / course</p>
        </div>
      </div>

      {currentDriver && (
        <div className="card mb-8 overflow-hidden relative"
          style={{ borderColor: currentDriver.color + '44' }}>
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(135deg, ${currentDriver.color}10 0%, transparent 60%)` }} />

          {/* Header */}
          <div className="flex items-center gap-5 relative z-10 mb-5">
            <div className="w-20 h-20 shrink-0 rounded-xl overflow-hidden"
              style={{ background: currentDriver.color + '20' }}>
              <img
                src={driverImgUrl(currentDriver.img)}
                alt={currentDriver.name}
                className="w-full h-full object-cover object-top"
                onError={e => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-widest mb-0.5" style={{ color: currentDriver.color }}>
                Pilote de saison · {currentDriver.team}
              </p>
              <p className="text-white font-black text-xl leading-tight">{currentDriver.name}</p>
              <p className="text-zinc-500 text-xs mt-0.5">#{currentDriver.number}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              {seasonDriverPoints > 0 && (
                <span className="text-lg font-black" style={{ color: currentDriver.color }}>+{seasonDriverPoints} pts</span>
              )}
              <Link to="/my-driver" className="btn-secondary text-xs py-1 px-3">Changer</Link>
            </div>
          </div>

          {/* Palmares */}
          {statsLoading && (
            <p className="text-zinc-500 text-xs">Chargement des stats...</p>
          )}
          {!statsLoading && driverStats && (
            <div className="relative z-10 border-t pt-4" style={{ borderColor: currentDriver.color + '30' }}>
              {/* Summary counters */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-xl p-3 text-center" style={{ background: currentDriver.color + '12' }}>
                  <p className="text-2xl font-black" style={{ color: currentDriver.color }}>
                    {driverStats.champPosition != null ? `P${driverStats.champPosition}` : '—'}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">Classement</p>
                </div>
                <div className="rounded-xl p-3 text-center" style={{ background: currentDriver.color + '12' }}>
                  <p className="text-2xl font-black text-white">{driverStats.totalPoints ?? '—'}</p>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">Points</p>
                </div>
                <div className="rounded-xl p-3 text-center" style={{ background: currentDriver.color + '12' }}>
                  <p className="text-2xl font-black text-white">{driverStats.wins}</p>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">Victoires</p>
                </div>
                <div className="rounded-xl p-3 text-center" style={{ background: currentDriver.color + '12' }}>
                  <p className="text-2xl font-black text-white">{driverStats.podiums}</p>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">Podiums</p>
                </div>
              </div>

              {/* Per-race results */}
              {driverStats.results?.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Résultats {SEASON}</p>
                  {driverStats.results.map((r) => (
                    <div key={r.round} className="flex items-center justify-between text-sm py-1 border-b border-zinc-800 last:border-0">
                      <span className="text-zinc-400 truncate">{r.raceName}</span>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        {r.points > 0 && <span className="text-[10px] text-zinc-500">+{r.points} pts</span>}
                        <span className={`font-black ${posColor(r.position)}`}>P{r.position}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {driverStats.racesRun === 0 && (
                <p className="text-zinc-600 text-xs">Aucun résultat disponible pour la saison {SEASON}.</p>
              )}
            </div>
          )}
        </div>
      )}

      <RivalSection myId={user?.id || user?._id} />

      {(() => {
        const allBadges = [
          ...newBadges.map(b => { const def = badgeDefs.find(d => d.id === b.id); return def ? { ...def, earnedAt: b.earnedAt } : null; }).filter(Boolean),
          ...badges.filter(b => !newBadges.find(nb => nb.id === b.id)),
        ];
        if (!allBadges.length) return null;
        return (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-white mb-3">Badges</h2>
            <div className="flex flex-wrap gap-3">
              {allBadges.map(b => (
                <div key={b.id} className="card flex items-center gap-3 py-2.5 px-3">
                  <span className="text-2xl">{b.icon}</span>
                  <div>
                    <p className="text-white text-sm font-black leading-tight">{b.label}</p>
                    <p className="text-zinc-500 text-[10px]">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Season prediction */}
      <div className="mb-8">
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <Icon name="trophy" size={18} /> Prédiction de champion {seasonInfo?.year || new Date().getFullYear()}
        </h2>
        {seasonPrediction ? (() => {
          const driverObj = F1_DRIVERS.find(d => d.name === seasonPrediction.driverPick);
          const constructorObj = F1_CONSTRUCTORS.find(c => c.name === seasonPrediction.constructorPick);
          return (
            <div className="card flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Icon name="check" size={13} className="text-green-400 shrink-0" />
                <span className="text-green-400 text-xs font-medium">
                  Soumise le {new Date(seasonPrediction.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                {seasonInfo?.resultsPublished && (
                  <span className={`ml-auto text-xl font-black ${ seasonPrediction.score > 0 ? 'text-yellow-400' : 'text-zinc-500'}`}>
                    {seasonPrediction.score} pts
                  </span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                {driverObj ? (
                  <div className="flex items-center gap-3 flex-1 rounded-xl px-3 py-2.5 border" style={{ borderColor: `${driverObj.color}44`, backgroundColor: `${driverObj.color}0d` }}>
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-800 border-2 shrink-0" style={{ borderColor: driverObj.color }}>
                      <img src={driverImgUrl(driverObj.img)} alt={driverObj.name} className="w-full h-full object-cover object-top" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: driverObj.color }}>Champion pilote</p>
                      <p className="text-white font-bold text-sm">{driverObj.name}</p>
                      <p className="text-zinc-500 text-xs">{driverObj.team}</p>
                    </div>
                    {seasonInfo?.resultsPublished && (
                      <span className="ml-auto text-xs font-bold" style={{ color: seasonPrediction.driverPick === seasonInfo.driverChampion ? '#4ade80' : '#f87171' }}>
                        {seasonPrediction.driverPick === seasonInfo.driverChampion ? '✓' : '✗'}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex-1 rounded-xl px-3 py-2.5 border border-zinc-800 flex items-center">
                    <p className="text-zinc-500 text-sm">Aucun pilote choisi</p>
                  </div>
                )}
                {constructorObj ? (
                  <div className="flex items-center gap-3 flex-1 rounded-xl px-3 py-2.5 border" style={{ borderColor: `${constructorObj.color}44`, backgroundColor: `${constructorObj.color}0d` }}>
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: constructorObj.color }} />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: constructorObj.color }}>Champion constructeur</p>
                      <p className="text-white font-bold text-sm">{constructorObj.name}</p>
                    </div>
                    {seasonInfo?.resultsPublished && (
                      <span className="ml-auto text-xs font-bold" style={{ color: seasonPrediction.constructorPick === seasonInfo.constructorChampion ? '#4ade80' : '#f87171' }}>
                        {seasonPrediction.constructorPick === seasonInfo.constructorChampion ? '✓' : '✗'}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex-1 rounded-xl px-3 py-2.5 border border-zinc-800 flex items-center">
                    <p className="text-zinc-500 text-sm">Aucun constructeur choisi</p>
                  </div>
                )}
              </div>
            </div>
          );
        })() : (
          <div className="card flex items-center justify-between gap-4">
            <p className="text-zinc-400 text-sm">Tu n'as pas encore soumis ta prédiction de champion de saison.</p>
            <Link to="/saison" className="btn-primary text-sm py-1.5 px-3 shrink-0">Prédire →</Link>
          </div>
        )}
      </div>

      <h2 className="text-lg font-bold text-white mb-4">Mes pronostics</h2>

      {loading ? (
        <div className="text-center text-zinc-400 py-12">Chargement...</div>
      ) : pronostics.length === 0 ? (
        <div className="card text-center py-12 text-zinc-400">
          <p className="mb-3">Aucun pronostic pour l'instant.</p>
          <Link to="/calendar" className="btn-primary inline-block">Voir le calendrier →</Link>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {pronostics.map(p => <PronosticCard key={p._id} pronostic={p} />)}
        </div>
      )}
    </div>
  );
};

export default Profile;
